// Per-campaign slice state, persisted across tab switches via a zustand store
// keyed by campaignId. The store initialises from a `CampaignConfig.inputs`
// array the first time the hook is called for a given campaign.

import { useEffect, useMemo } from 'react';
import { create } from 'zustand';

import type { AxisSpec, SliceState } from '../types';

type CampaignKey = string;

type SliceStore = {
  byCampaign: Record<CampaignKey, SliceState>;
  ensure: (id: CampaignKey, inputs: readonly AxisSpec[]) => void;
  update: (id: CampaignKey, patch: Partial<SliceState>) => void;
  replace: (id: CampaignKey, next: SliceState) => void;
};

const midpoint = (axis: AxisSpec): number => {
  if (axis.mode === 'fixed') return axis.fixedValue ?? 0;
  if (axis.min === undefined || axis.max === undefined) return 0;
  return 0.5 * (axis.min + axis.max);
};

/**
 * Build the default SliceState for a campaign: pick the first two
 * range-mode inputs as x/y, pin the remainder at their midpoint, and use a
 * 10% slab thickness.
 */
export function buildDefaultSliceState(inputs: readonly AxisSpec[]): SliceState {
  const rangeInputs = inputs.filter((a) => a.mode === 'range');
  const xAxis = rangeInputs[0]?.name ?? '';
  const yAxis = rangeInputs[1]?.name ?? '';
  const pinned: Record<string, number> = {};
  for (const axis of rangeInputs) {
    if (axis.name === xAxis || axis.name === yAxis) continue;
    pinned[axis.name] = midpoint(axis);
  }
  return {
    xInput: xAxis,
    yInput: yAxis,
    pinned,
    slabFraction: 0.1,
  };
}

const useSliceStore = create<SliceStore>((set) => ({
  byCampaign: {},
  ensure: (id, inputs) =>
    set((s) => {
      // Wait until the caller actually has axis metadata; otherwise we'd
      // persist a blank { xInput: '', yInput: '' } slice which then sticks
      // because of the existence check below, causing surface queries to
      // fail with "xInput/yInput required" and the panel to hang on
      // "Loading surface…" after campaignState eventually lands.
      const hasAxes = inputs.some((a) => a.mode === 'range');
      if (!hasAxes) return s;
      const existing = s.byCampaign[id];
      if (existing && existing.xInput) return s;
      return {
        ...s,
        byCampaign: { ...s.byCampaign, [id]: buildDefaultSliceState(inputs) },
      };
    }),
  update: (id, patch) =>
    set((s) => {
      const current = s.byCampaign[id];
      if (!current) return s;
      const next: SliceState = {
        ...current,
        ...patch,
        pinned: { ...current.pinned, ...(patch.pinned ?? {}) },
      };
      return { ...s, byCampaign: { ...s.byCampaign, [id]: next } };
    }),
  replace: (id, next) =>
    set((s) => ({ ...s, byCampaign: { ...s.byCampaign, [id]: next } })),
}));

export type UseSliceStateResult = {
  slice: SliceState;
  setSlice: (next: SliceState) => void;
  patchSlice: (patch: Partial<SliceState>) => void;
};

export function useSliceState(
  campaignId: string,
  inputs: readonly AxisSpec[],
): UseSliceStateResult {
  const ensure = useSliceStore((s) => s.ensure);
  const replace = useSliceStore((s) => s.replace);
  const update = useSliceStore((s) => s.update);
  const stored = useSliceStore((s) => s.byCampaign[campaignId]);

  useEffect(() => {
    ensure(campaignId, inputs);
  }, [campaignId, inputs, ensure]);

  const fallback = useMemo(() => buildDefaultSliceState(inputs), [inputs]);
  const slice = stored ?? fallback;

  return {
    slice,
    setSlice: (next) => replace(campaignId, next),
    patchSlice: (patch) => update(campaignId, patch),
  };
}

// Exposed for tests.
export const __internal = { useSliceStore };
